// Static docs pages. Add an entry here and it shows up at /docs and /docs/<slug>.
//
// SYNC: when you add, remove, or retitle a post, mirror it in `posts` in
// public/seo-manifest.json. That file is what the backend reads (it can't import this one — it's
// TypeScript, and only the built output ships in the image), and it drives both the page's
// server-rendered <title>/description and its sitemap.xml entry. A post missing from the manifest
// still renders fine for users but is invisible to search engines.

export type BlogPost = {
  slug: string;
  title: string;
  description: string;
  readMinutes: number;
  content: string; // markdown
};

export const BLOGS: BlogPost[] = [
  {
    slug: "getting-started",
    title: "Quick Setup Guide",
    description:
      "A step-by-step setup guide: create an account, connect your agent, and install your first skill in about five minutes.",
    readMinutes: 3,
    content: `# Getting Started with SkillPatch

This guide walks you through creating an account, connecting your agent, and installing your first
skill. It takes about five minutes.

If you're new to skills in general, read [What Are Agent Skills?](/docs/what-are-skills) first. For a
full walkthrough of everything SkillPatch can do, see [How to Use SkillPatch](/docs/how-to-use-skillpatch).

## Step 1: Create your account

Sign up and log in. Your account comes with an API key, which you'll use to connect your agent and to
install or publish private skills.

## Step 2: Connect your agent

Connecting your agent lets you install and publish skills with a single command. Open Settings, copy
your setup command, and run it in your terminal.

\`\`\`bash
# macOS / Linux
curl -sSL <base>/setup/<key> | bash

# Windows (PowerShell)
irm <base>/setup/<key> | iex
\`\`\`

This installs the SkillPatch skill, saves your API key, and points your agent at the registry. To
check that it worked, run \`/skillpatch browse\`. If the catalog loads, you're connected.

## Step 3: Install a skill

There are two ways to install a skill.

From your agent, run \`/skillpatch install <slug>\`. The skill downloads into your skills folder and is
ready to use.

From the web, open a skill on the Explore page and copy the command it shows you:

\`\`\`bash
mkdir -p .claude/skills
curl -sSL <host>/install_skill/<slug> | tar -xz -C .claude/skills/
\`\`\`

If you're not sure what to install first, browse the Trending and Official tabs. Skills marked
"Verified by SkillPatch" have passed a security review.

## Step 4: Publish a skill (optional)

Have a skill of your own to share? Go to Add a skill and upload a \`.tar\`, \`.tgz\`, or \`.zip\` file, or
import one from GitHub. Set it to public or private, choose a domain, and add a few tags. SkillPatch
runs it through a scan and security check, and once it passes it shows up on your dashboard.

You can also publish the skill you're currently working on by running \`/skillpatch save\`.

## Next steps

That's the setup. You now have an account, a connected agent, and your first skill installed.

To see what else SkillPatch can do, including versioning, suggestions, teams, and GitHub sync, read
[How to Use SkillPatch](/docs/how-to-use-skillpatch). Your dashboard keeps everything you own,
install, and share in one place.
`,
  },
  {
    slug: "what-are-skills",
    title: "What Are Agent Skills?",
    description:
      "Learn what agent skills are, and how they help AI agents perform tasks consistently through reusable capabilities.",
    readMinutes: 6,
    content: `# What Are Agent Skills?

AI agents can perform many tasks, but they don't automatically know how you want those tasks done. Without reusable instructions, you end up repeating the same prompts, workflows, and formatting rules over and over.

**Agent skills** solve this problem. A skill is a reusable capability that teaches an agent how to perform a specific task consistently. Instead of rewriting instructions every time, you package them once and let the agent use them whenever they're needed.

In this article, you'll learn what a skill is, what's inside a skill, how an agent uses it, and why skills are a fundamental building block for creating reliable AI agents.


## The one-sentence definition

> A **skill** is a self-contained package of instructions and supporting files that an agent
> can load to perform a specific task.

Think of it like a recipe card the agent can pick up when it's relevant: it describes *what* the
task is, *when* to use it, and the *steps* to follow.

## What's inside a skill

Every skill contains instructions that teach an agent. At its root is a single Markdown file  **\`SKILL.md\`**  which will be an entry point that
describes the capability. It usually opens with a little metadata block (front-matter), followed by
the actual instructions:

\`\`\`markdown
---
name: commit-helper
description: Writes clean, conventional commit messages from a staged diff. Use when the user
  wants help committing changes.
---

# Commit Helper

1. Read the staged diff.
2. Summarize the change in the imperative mood ("Add", "Fix", "Refactor").
3. Output a single conventional-commit message: \\\`type(scope): summary\\\`.
\`\`\`

Two fields do the heavy lifting:

- **name :**  a short identifier for the skill.
- **description :** one or two sentences saying *what it does* and *when to use it*. This is the
  most important line in the whole file (more on that below).

A skill can also bundle **supporting files** next to \`SKILL.md\` commonly:

- \`scripts/\` : small programs the instructions tell the agent to run.
- \`references/\` : deeper documentation the agent can open when it needs detail.
- \`assets/\` : templates, examples, or files the task produces or copies.

The whole folder travels together as one unit.

## How an agent actually uses one

Skills are designed around **progressive disclosure** the agent doesn't read everything up front.
It works in layers:

1. The agent is aware of each installed skill by its **name + description** only (cheap to keep in
   context).
2. When your request matches a skill's description, the agent **opens its \`SKILL.md\`** and follows
   the instructions.
3. If the instructions point to a script or a reference file, the agent **pulls that in too** only
   when it's actually needed.

This keeps the agent's working context lean while still giving it access to a large library of
know-how. Most agent runtimes load skills from a dedicated folder (for example, a \`skills/\`
directory the agent scans at startup), so "installing" a skill is often as simple as dropping the
folder in the right place.

## Why the description matters so much

An agent decides whether to use a skill based primarily on its description. A vague description makes it difficult for the agent to recognize when the skill is relevant.
Compare:

- ❌ \`description: Helps with git.\`
- ✅ \`description: Writes conventional commit messages from a staged diff. Use when the user is
  about to commit and wants a well-formed message.\`

The second one tells the agent exactly **when** to reach for it. Write descriptions for the agent's
decision, not for a human skimming a list.

## Skills vs. prompts vs. tools

It's easy to confuse skills with neighboring ideas:

- A **prompt** is a one-off instruction you type now. A **skill** is a *saved, reusable* procedure
  the agent applies whenever it fits.
- A **tool** (or function call) lets an agent *do* something, hit an API, run code. A **skill** is
  *know-how*: it can tell the agent which tools to use and in what order, but it's primarily
  instructions, not an executable interface.
- Skills are **model-agnostic know-how** plain files. They don't replace tools or memory; they
  organize procedure.

## Why they matter

- **Reusable :** capture a procedure once; stop re-explaining it.
- **Consistent :** every run follows the same steps, so output quality is repeatable.
- **Composable :** a library of small, focused skills beats one giant prompt.
- **Shareable :** because a skill is just a folder of text, you can hand it to a teammate, commit it
  to a repo, or publish it for others.
- **Lean :** progressive disclosure means more capability without bloating the agent's context.

## In short

Agent skills capture repeatable workflows as reusable capabilities. Instead of teaching an AI agent the same process every time, you teach it once and let it reuse that knowledge whenever it's relevant.

As your library of skills grows, your agents become more consistent, more capable, and much easier to extend.

Ready to write your first skill? Read **[How to Write a Skill](/docs/how-to-create-a-skill)**.
`,
  },
  {
    slug: "how-to-create-a-skill",
    title: "How to Write a Skill",
    description:
      "Learn how to create your first agent skill, structure SKILL.md, and write instructions that agents can reliably follow.",
    readMinutes: 7,
    content: `# How to Write a Skill


A good skill isn't about writing lots of instructions it's about clearly describing a single task so an AI agent can perform it consistently. In this guide, we'll build a skill from scratch, structure \`SKILL.md\`, and cover the practices that make skills reliable and reusable.
Here's how to write one from scratch.

## 1. Scope it to a single job

The best skills do **one thing**. "Write conventional commit messages" is a skill. "Manage my
entire git workflow" is five skills wearing a trenchcoat. A tight scope makes the instructions
short, the behavior predictable, and the skill easy to reuse.

Before writing anything, finish this sentence: *"This skill helps an agent ______ when ______."*
If you need an "and" in the first blank, consider splitting it.

## 2. Create the skill structure

Create a folder and add a \`SKILL.md\` at its root. Begin with front-matter, then the body:

\`\`\`markdown
---
name: changelog-writer
description: Turns a git diff and a version number into a clean changelog entry. Use when
  preparing release notes.
---

# Changelog Writer

## When to use
Use this when the user has a set of merged changes and wants a release/changelog entry.

## Steps
1. Read the staged or committed diff.
2. Group user-facing changes by type: Added, Fixed, Changed, Removed.
3. Write each item in past tense, one line each, under the correct heading.
4. Put everything under a \\\`## <version> — <date>\\\` heading.

## Example
\\\`\\\`\\\`
## 1.4.0 — 2026-01-12
### Added
- Dark mode toggle in settings.
### Fixed
- Crash when importing empty CSV files.
\\\`\\\`\\\`
\`\`\`

### Give your skill a clear identity

- **name :**a short, stable identifier. The common convention is **lowercase-with-hyphens**
  (\`changelog-writer\`), no spaces.
- **description :** the single most important line. State **what it does and when to use it**, in
  plain language, because the agent uses this to decide whether to reach for the skill at all. Be
  specific: include the trigger ("use when…").

## 3. Write instructions an agent can execute

Agents follow **concrete, ordered steps** far better than prose. Good instructions are:

- **Sequential :** numbered steps, in the order they should happen.
- **Specific :** "summarize in the imperative mood" beats "make it nice".
- **Bounded :** say what's in scope *and* what to skip ("ignore whitespace-only changes").
- **Exemplified :** one short input→output example removes a world of ambiguity.

Cover the **edge cases** you care about: empty input, multiple items, conflicting options. If the
agent shouldn't do something, say so explicitly.

## 4. Add supporting files (only if they help)

Many skills only need a \`SKILL.md\`. If your skill grows, you can add supporting files such as scripts, documentation, or templates and reference them from your instructions.

- \`scripts/\` : a script the agent should *run* rather than reimplement. Reference it from the
  body: *"Run \\\`scripts/format.py\\\` on the diff."*
- \`references/\` : longer documentation the agent opens *when needed* (a style guide, an API
  reference). Keep it out of \`SKILL.md\` so the main file stays short.
- \`assets/\` : templates or example files the task copies or fills in.

**Rule:** every file you mention in \`SKILL.md\` must actually exist in the folder, and every file in
the folder should be referenced or clearly useful. Dangling references confuse the agent.

## 5. Keep SKILL.md lean (progressive disclosure)

Keep \`SKILL.md\` focused. Move large references into separate files so the main instructions stay easy to read and maintain.


## 6. Test it like a user would

Install the skill into your agent and try the **real prompts** people will use including messy
ones. Watch for:

- Did the agent **trigger** the skill at the right time? If not, sharpen the **description**.
- Did it **follow every step**? If it skipped one, make the step more explicit or reorder.
- Did it handle the **edge cases**? Add them to the instructions if not.

Iterate on the wording until the behavior is reliable. Skills are prompt-engineering you can save.

## A quick checklist

- [ ] One clear job, one clear trigger.
- [ ] \`name\` is short and lowercase-hyphenated.
- [ ] \`description\` says *what* and *when*.
- [ ] Steps are numbered, specific, and bounded.
- [ ] At least one example.
- [ ] Every referenced file exists; no dead links.
- [ ] \`SKILL.md\` stays focused; depth lives in \`references/\`.
- [ ] Tested against real, messy prompts.

Writing a good skill is about clarity, not complexity. Focus on one task, explain it with clear, step-by-step instructions, and test it with real prompts until the behavior is consistent.
Once your skill works, you'll probably want to **share it** that's what a skill registry like
**[SkillPatch](/docs/how-to-use-skillpatch)** is for.
`,
  },
  {
    slug: "how-to-use-skillpatch",
    title: "How to Use SkillPatch",
    description:
      "Learn how to connect SkillPatch to your agent, discover and install skills, publish your own, and collaborate with others through a shared skill registry.",
    readMinutes: 7,
    content: `# How to Use SkillPatch

Creating a skill is only the first step. Once you have a growing collection of skills, you'll want an easy way to discover, install, update, and share them with others.

That's where **SkillPatch** comes in. SkillPatch is a registry for agent skills, making it easy to manage reusable capabilities just as package registries help developers manage software libraries.

In this guide, you'll learn how to connect SkillPatch to your agent, install skills, publish your own, and collaborate with your team.


> Just want to get set up fast? The **[Quick Setup Guide](/docs/getting-started)** covers your
> account, connecting your agent, and your first install. New to skills themselves? Start with
> **[What Are Agent Skills?](/docs/what-are-skills)** and **[How to Write a Skill](/docs/how-to-create-a-skill)**.
> This post is the complete reference.

## Work from your agent

If you haven't connected SkillPatch yet, the **[Quick Setup Guide](/docs/getting-started)** walks you
through it. Once your agent is connected, it stores your API key and the registry address, so installs,
uploads, and suggestions are all authenticated automatically.

From then on you can drive the whole registry with slash commands:

- \`/skillpatch browse [query]\` searches the catalog
- \`/skillpatch install <slug>\` installs a skill
- \`/skillpatch save\` publishes the skill you're working on
- \`/skillpatch suggest <slug>\` proposes a change
- \`/skillpatch my-skills\` lists what you own
- \`/skillpatch rate <slug>\` leaves a star rating

You can do all of this in the web app too; the commands just mirror the pages below.

## Discover skills on Explore

Browse the Explore page to search skills by name, description, or tags. You can discover trending community skills, official publisher skills, or your own library, then review ratings, documentation, and files before installing.

Every skill has a detail page with its \`SKILL.md\`, a browsable **file tree**, its metrics, and
community **star ratings**, so you can judge quality before you install. See something you like but
aren't ready for? Hit **Bookmark** to save it for later. Bookmarks get their own spot on your
dashboard.

## Trust the security badges

Every published skill is automatically scanned for common security risks before it's made available. Verified skills receive a badge, while potentially unsafe skills are clearly flagged before installation.

## Install in one line

Installing a skill should feel as easy as installing a package. With SkillPatch, you can install directly from your agent or copy a platform-specific command from the web interface.

\`\`\`bash
mkdir -p .claude/skills
curl -sSL <host>/install_skill/<slug> | tar -xz -C .claude/skills/
\`\`\`

The skill lands in your agent's skills folder and is ready immediately. Or just tell your agent to run
\`/skillpatch install <slug>\`. A few details worth knowing:

- **Private skills** add your API key to the command automatically, so only people with access can
  install them.
- **Any version is installable forever.** Grab the latest, or **pin an exact version** so updates never
  pull the rug out from under you.

## Publish your own

Ready to share a skill? Open **Add a skill** and choose a source:

- **Upload an archive:** a \`.tar\`, \`.tar.gz\`, \`.tgz\`, or \`.zip\` (up to 10 MB).
- **Import from GitHub:** connect GitHub in Settings, then point at a repo, branch, and subpath.

Then set **visibility** (public or private), pick up to **two domains** so it's categorized, and add
up to **four tags**. After uploading, SkillPatch validates the skill, performs automated security checks, and publishes it once everything passes.

If something's wrong, you get a **clear rejection report** with the exact issues to fix instead of a
mystery failure.

## Versioning that never breaks downstream

Skills evolve, so on SkillPatch **every change is a new version** and history is **append-only**:

- Every update creates a new version.
- Older versions remain installable.
- You can restore previous versions.
- Every version has its own **share** link and install command.

## Improve skills with suggestions

This is where SkillPatch shines for collaboration. On any skill that accepts them, anyone, whether a
person *or* an agent, can open a **suggestion**, SkillPatch's version of a pull request:

- **Describe the change** in plain English and a **feedback agent** turns it into concrete file edits, or
- **Upload your edited files** directly if you'd rather make the changes yourself.

Here's the flow. The suggestion arrives for the owner, who clicks **Process** to run the review
pipeline (**Feedback → Critic → Security**), which prepares the edits, checks quality, and re-scores
safety. The owner then sees a **git-style diff** of exactly what changed and chooses **Approve &
merge** or **Reject**. Approve, and it becomes a new version that **credits the contributor**; merged
work shows up under their **Contributions**. Reject, and nothing changes. (If the skill is linked to
GitHub and both sides changed, SkillPatch flags a **conflict** and walks you through resolving it.)

## Collaborate with your team

Not every skill belongs in a public registry. SkillPatch lets you keep skills private and share them securely with the people who need them.

Share skills with individual users or entire teams, assign read or write access, and collaborate without exposing internal workflows. Whether you're building internal engineering tools or company-specific automations, your skills stay accessible only to the right people.

## Extend existing skills

You don't always have to start from scratch. If you find a community skill that's close to what you need, you can **fork** it and build your own version while preserving a link to the original.

If SkillPatch has already indexed a skill from your public GitHub repository, you can **claim** ownership to publish updates under your account. You can also connect a skill to GitHub to keep both versions synchronized and raise pull requests directly from SkillPatch.

## Manage everything from one place

Your dashboard brings together everything related to your skills in one place. Track the skills you've published, browse skills shared with you, review incoming suggestions, manage teams, revisit bookmarked skills, and stay on top of notifications all without switching between multiple pages.

## Built for individuals and teams

Whether you're building skills for yourself, sharing them within your organization, or publishing them for the community, SkillPatch provides the tools to manage the entire lifecycle. Private sharing, version history, collaboration, and GitHub integration make it easy to grow a library of reusable skills that scales from a single developer to an entire team.


## Wrapping up

That's the full picture: discover, install, publish, version, secure, and improve skills, on your own
or with a team. SkillPatch gives your skills a central place to live, evolve, and get reused across
projects.

Haven't set up yet? **[Read about Quick Setup →](/docs/getting-started)** to get connected and
installing in a few minutes.
`,
  },
];

export function getBlog(slug: string): BlogPost | undefined {
  return BLOGS.find((b) => b.slug === slug);
}
